import ArrowDownward from "@mui/icons-material/ArrowDownward";
import { Box, Collapse, Divider, Typography } from "@mui/material";
import type { FC } from "react";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useGetResolverJobDisplayName } from "@/hooks/useJobDisplayName";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { FONT_SIZE, NEUTRAL } from "@/theme/tokens";
import { NotificationType, type OpenShiftRequest, type ReplaceShiftRequest, RequestResponsesStatusOptions } from "@/utils/models";
import { inverseRolesMap, type RoleFullKey } from "@/utils/utilFunctions";

interface IProps {
	dayRequests: (OpenShiftRequest | ReplaceShiftRequest)[];
	onExpandRequest: (event: React.MouseEvent<HTMLElement>) => void;
	onRequestClick: (event: React.MouseEvent<HTMLElement>, request: OpenShiftRequest | ReplaceShiftRequest) => void;
	showRequests: boolean;
}

export const DayRequests: FC<IProps> = ({ dayRequests, onExpandRequest, showRequests, onRequestClick }) => {
	const jobFlagActive = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const resolveJobDisplayName = useGetResolverJobDisplayName();
	const formatRequest = (request: OpenShiftRequest | ReplaceShiftRequest): string => {
		const acceptedNurses: number = request.message.request_data.filter((requestData) => requestData.status === RequestResponsesStatusOptions.PENDING).length;

		switch (request.request_type) {
			case NotificationType.OpenShift: {
				const message = (request as OpenShiftRequest).message.roles.map((role) => inverseRolesMap[role as RoleFullKey]).join(" ");

				return `${jobFlagActive && request.job_id ? resolveJobDisplayName(request.job_id) : message} - ${acceptedNurses} Responded`;
			}
			case NotificationType.ReplaceShift:
				return `Replace - ${acceptedNurses} Responded`;
			case NotificationType.ReplaceAgency:
				return `Replace Agency - ${acceptedNurses} Responded`;
			default:
				return `${acceptedNurses} Responded`;
		}
	};

	if (dayRequests.length === 1) {
		return (
			<Box
				data-testid="day-request"
				sx={{
					"&:hover": {
						bgcolor: NEUTRAL[50],
					},
					transition: "background-color 0.2s ease",
					px: 1,
					borderRadius: "4px",
					cursor: "pointer",
				}}
				onClick={(e: React.MouseEvent<HTMLElement>) => onRequestClick(e, dayRequests[0])}
			>
				<Typography
					sx={{
						fontWeight: 400,
						fontSize: FONT_SIZE.EMPHASIS,
						color: "primary.dark",
						height: "28px",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						pt: "4px",
					}}
				>
					{formatRequest(dayRequests[0])}
				</Typography>
				<Divider />
			</Box>
		);
	}

	return (
		<>
			{dayRequests.length > 0 ? (
				<Box sx={{ px: 1, borderRadius: "4px" }}>
					<Box>
						<Box
							data-testid="day-requests-expand"
							sx={{
								display: "flex",
								alignItems: "center",
								cursor: "pointer",
								justifyContent: "space-between",
								"&:hover": {
									bgcolor: NEUTRAL[50],
								},
								transition: "background-color 0.2s ease",
								borderRadius: "4px",
							}}
							onClick={onExpandRequest}
						>
							<Typography
								sx={{
									fontWeight: 400,
									fontSize: FONT_SIZE.EMPHASIS,
									color: "primary.dark",
									height: "28px",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									width: "100%",
									pt: "4px",
								}}
							>
								{dayRequests.length} Pending Requests
							</Typography>
							<ArrowDownward
								className="arrow-icon"
								sx={{
									color: "primary.main",
									transform: showRequests ? "rotate(180deg)" : "rotate(0deg)",
									transition: "transform 0.3s ease, opacity 0.2s ease",
									fontSize: 16,
								}}
							/>
						</Box>
						<Collapse in={showRequests}>
							{dayRequests.map((request) => (
								<Box
									key={request.id}
									data-testid="day-request"
									sx={{
										cursor: "pointer",
										"&:hover": {
											bgcolor: NEUTRAL[50],
										},
										transition: "background-color 0.2s ease",
										borderRadius: "4px",
									}}
									onClick={(e: React.MouseEvent<HTMLElement>) => onRequestClick(e, request)}
								>
									<Divider />
									<Typography
										sx={{
											fontWeight: 400,
											fontSize: FONT_SIZE.EMPHASIS,
											color: "primary.dark",
											height: "28px",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											pt: "4px",
										}}
									>
										{formatRequest(request)}
									</Typography>
								</Box>
							))}
						</Collapse>
						<Divider />
					</Box>
				</Box>
			) : (
				<Box
					sx={{
						height: "29px",
						"&:hover": {
							bgcolor: NEUTRAL[50],
						},
						transition: "background-color 0.2s ease",
						px: 1,
						borderRadius: "4px",
					}}
				>
					<Typography
						sx={{
							fontWeight: 400,
							fontSize: FONT_SIZE.EMPHASIS,
							color: "primary.dark",
							pt: "4px",
						}}
					>
						--
					</Typography>
					<Divider />
				</Box>
			)}
		</>
	);
};
